// Coupon management redesign (Phase 2 PR 4, 2026-05-15):
//   coupon_templates 管理画面 — 一覧 + landmark filter + Create/Edit form (sheet) + Delete。
//   既存 LandmarkManagementPage / TierPresetManagementPage パターン踏襲。
//
// Spec: design/coupon-management-redesign-spec.md §CMS 入稿 UX
//
// auth: ADMIN (全 template) / BUSINESS (自分が sponsor_user_id の template のみ)。
//   backend (CouponTemplatesService.list) が role に応じて自動 filter。
//
// Phase 2 PR 5 で「code 入稿 UI」 / PR 6 で「Code 一覧 + dashboard stats」 / PR 7 で「CSV export」を追加予定。
// 本 PR は template の CRUD のみ (= code list / batch 入稿 は本 PR では非対応)。

import { Button } from '@/components/common/Button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, Upload, List, Download, AlertTriangle } from 'lucide-react';
import CouponTemplateFormSheet from './components/CouponTemplateFormSheet';
import CouponBatchCodeUploadSheet from './components/CouponBatchCodeUploadSheet';
import CouponBatchCodeListSheet from './components/CouponBatchCodeListSheet';

// fortune key → label (= spec §24.1 注釈、5 階層 = DAIKICHI..KYO)。
const FORTUNE_LABELS: Record<string, string> = {
    '1': '大吉',
    '2': '中吉',
    '3': '小吉',
    '4': '末吉',
    '5': '凶'
};
const FORTUNE_COMMON_LABEL = '共通プール';

const formatFortune = (f: number | null | undefined): string => {
    if (f === null || f === undefined) return FORTUNE_COMMON_LABEL;
    return FORTUNE_LABELS[String(f)] ?? `(${f})`;
};

const formatDate = (s: string | null | undefined): string => {
    if (!s) return '-';
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return s;
        return d.toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
        return s;
    }
};

const ALL_LANDMARKS_SENTINEL = '__all__';

export default observer(function CouponTemplateManagementPage() {
    const {
        couponTemplateStore: { templates, total, list: fetchList, remove: removeTemplate, exportCodes },
        landmarkStore: { landmarks, list: fetchLandmarkList },
        modalStore: { showAlertModal }
    } = useStore();

    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
    const [editTarget, setEditTarget] = useState<any | null>(null);
    const [landmarkFilter, setLandmarkFilter] = useState<string>(ALL_LANDMARKS_SENTINEL);
    // Phase 2 PR 5 (2026-05-15): batch code 入稿 sheet
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadTarget, setUploadTarget] = useState<any | null>(null);
    // Phase 2 PR 6 (2026-05-15): code 一覧 sheet
    const [listOpen, setListOpen] = useState(false);
    const [listTarget, setListTarget] = useState<any | null>(null);

    const reload = () => {
        const params = landmarkFilter === ALL_LANDMARKS_SENTINEL ? {} : { landmark_id: landmarkFilter };
        return flowResult(fetchList(params));
    };

    useEffect(() => {
        fetchLandmarkList();
    }, []);

    useEffect(() => {
        reload();
    }, [landmarkFilter]);

    const landmarkNameById = useMemo(() => {
        const m: Record<string, string> = {};
        for (const lm of landmarks) m[String(lm.id)] = lm.name;
        return m;
    }, [landmarks]);

    const handleOpenCreate = () => {
        setSheetMode('create');
        setEditTarget(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (tpl: any) => {
        setSheetMode('edit');
        setEditTarget(tpl);
        setSheetOpen(true);
    };

    const handleOpenUpload = (tpl: any) => {
        setUploadTarget(tpl);
        setUploadOpen(true);
    };

    const handleOpenList = (tpl: any) => {
        setListTarget(tpl);
        setListOpen(true);
    };

    const handleExport = async (tpl: any) => {
        // ApiStore.call が Content-Type を見て FileSaver.saveAs を自動発火するため、
        // 呼ぶだけで download される (= cms 側で URL 組立や a.click 不要)。
        const res = await flowResult(exportCodes({ id: tpl.id }));
        if (!res || res?.ok === false) {
            toastify('error', res?.message || 'CSV エクスポートに失敗しました');
        }
    };

    const handleDelete = (tpl: any) => {
        showAlertModal({
            title: '削除確認',
            size: 'sm',
            content: `「${tpl.name}」を削除しますか?\n紐付け済みクーポンコード (= coupon_batch_codes) は CASCADE で削除されます。\n(paranoid 削除のため復元可能)`,
            onSave: async () => {
                const res = await flowResult(removeTemplate(tpl.id));
                if (res?.ok) {
                    toastify('success', '削除しました');
                    reload();
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    return (
        <div className='layout-card'>
            {/* 2026-05-16 (2 段階 shake redesign): 統一コード方式 + 2 段階 shake の運用ガイド。
                spec §6-3 で全文確定済。 */}
            <details className='mb-4 border border-blue-200 bg-blue-50 rounded text-sm'>
                <summary className='cursor-pointer px-3 py-2 font-medium text-blue-900'>
                    🛈 操作ガイド (= クーポン管理の基本)
                </summary>
                <div className='px-3 pb-3 space-y-4 text-blue-900'>
                    <div>
                        <p className='font-semibold mb-1'>① 2 つの「コード方式」 から選択 (デフォルト: 統一コード方式)</p>
                        <ul className='list-disc pl-5 space-y-0.5 text-xs'>
                            <li>
                                <strong>統一コード方式 ★デフォルト</strong>
                                : 全利用者に同じコードを配布、 期間 / 配布上限内で運用。 1 つのコードを店頭で確認する運用に最適。
                            </li>
                            <li>
                                <strong>個別コード方式</strong>
                                : POS で 1 枚 1 枚を追跡 (= 番号付きクーポン券 / スクラッチカード等)。 1 user 1 code 運用に最適。
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p className='font-semibold mb-1'>② 通常のクーポン配布 (= シェイクで 1 枚配る)</p>
                        <ol className='list-decimal pl-5 space-y-0.5'>
                            <li>「新規追加」 でクーポンテンプレートを作成</li>
                            <li>種別は「通常クーポン」、 コード方式は**統一コード方式が初期選択** (= 切替で個別コード方式も可)</li>
                            <li>統一の場合: 配布したいコードと配布上限 (= 任意で「無制限」 も可) を入力</li>
                            <li>個別の場合: 「↑」 アイコンで POS 登録済みコードを CSV / 直接入力で投入</li>
                            <li>利用者がアプリでシェイク → 1 枚自動配布</li>
                        </ol>
                    </div>
                    <div>
                        <p className='font-semibold mb-1'>③ レビュー後に「アップグレードクーポン」 を 2 枚目として配る運用</p>
                        <p className='text-xs mb-2'>
                            利用者がレビューを送信した後、 もう一度シェイクすると 2 枚目のクーポン (= アップグレード) が postcard で届きます。
                            <br />
                            例: 通常クーポン「10% off」 → レビュー送信 → もう一度シェイクで「20% off」 が 2 枚目として届く。
                            1 枚目 (10% off) は**無効化済**として履歴に残ります。
                        </p>
                        <ol className='list-decimal pl-5 space-y-0.5'>
                            <li>
                                <span>クーポンテンプレートを 2 つ用意:</span>
                                <ul className='list-disc pl-5 mt-0.5'>
                                    <li>通常クーポン (= 種別「通常クーポン」、 最初のシェイクで配るもの)</li>
                                    <li>アップグレードクーポン (= 種別「アップグレードクーポン」、 レビュー後の 2 枚目用)</li>
                                </ul>
                                <span>コード方式は各 template ごとに選択可能 (= 通常を個別、 アップグレードを統一、 等の混合 OK)</span>
                            </li>
                            <li>通常クーポンの「✏」 (編集) を開き、「レビュー後に切替えるクーポン」 でアップグレードクーポンを選択</li>
                            <li>「ランドマーク管理」 で対象 landmark を開き、「レビュー特典の種類」 を「クーポンを切替える」 に設定</li>
                            <li>
                                <span>利用者の流れ:</span>
                                <ul className='list-disc pl-5 mt-0.5'>
                                    <li>シェイク 1 回目 → 通常クーポン取得 (= postcard 1 枚目)</li>
                                    <li>レビュー送信 → 1 枚目が「無効化済」 と表示</li>
                                    <li>30 分以内に同 landmark で再度シェイク → アップグレードクーポン取得 (= postcard 2 枚目)</li>
                                </ul>
                            </li>
                            <li>
                                <span>在庫切れ時の挙動:</span>
                                <ul className='list-disc pl-5 mt-0.5'>
                                    <li>アップグレードクーポンの在庫が無い時は、 利用者の 2 枚目シェイク時に「在庫切れ」 と表示</li>
                                    <li>同時に 1 枚目 (通常クーポン) の「無効化」 が解除され、 元のクーポンが再度使える状態に戻ります</li>
                                    <li>2 枚目シェイクが 30 分以内に行われなかった場合は、 利用者の 1 枚目は「無効化済」 のまま残ります</li>
                                </ul>
                            </li>
                        </ol>
                    </div>
                    <div>
                        <p className='font-semibold mb-1'>④ 用語整理</p>
                        <ul className='list-disc pl-5 space-y-0.5'>
                            <li>
                                <strong>種別</strong>
                                : 「通常クーポン」 (= シェイク 1 で配布) / 「アップグレードクーポン」 (= レビュー後のシェイク 2 で配布)
                            </li>
                            <li>
                                <strong>コード方式</strong>
                                : 「統一コード方式」 (= 全 user 同じ code + 配布上限) / 「個別コード方式」 (= 1 user 1 code)
                            </li>
                            <li>
                                <strong>名称</strong>
                                : CMS 一覧で識別する内部名 (= 利用者には表示されない)
                            </li>
                            <li>
                                <strong>説明 ja/en</strong>
                                : 利用者のアプリ画面、 クーポンコードの下に表示される文言 (= 「対象店舗で 10% off」 等)
                            </li>
                            <li>
                                <strong>等級 (fortune)</strong>
                                : 大吉 / 中吉 / 小吉 / 末吉 / 凶 の 5 階層、 または「共通」 (= どの等級でも配布)
                            </li>
                            <li>
                                <strong>在庫表示</strong>
                                :
                                <ul className='list-disc pl-5 mt-0.5'>
                                    <li>統一方式: 「配布数 N / M (残 X)」 形式、 残 0 で在庫切れ、 残 1-19 で補充推奨。 無制限の場合は「配布数 N / 無制限」</li>
                                    <li>個別方式: 0=赤「在庫切れ」 / 1-19=黄「補充推奨」 / 20+=緑「健全」</li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>
            </details>
            <div className='flex items-center justify-between mb-4'>
                <div>
                    <h2 className='text-[18px] font-bold'>クーポンテンプレート管理</h2>
                    <p className='text-sm text-gray-500 mt-1'>
                        登録件数:
                        {' '}
                        {total}
                        {' '}
                        / 事業者が手元の POS / 紙券に印字されたクーポンコードを CMS にまとめて投入し、利用者の shake で 1 件ずつ配ります。
                    </p>
                </div>
                <Button variant='active' onClick={handleOpenCreate}>
                    新規追加
                </Button>
            </div>

            <div className='mb-4 flex items-center gap-3'>
                <label className='text-sm text-gray-600'>landmark 絞り込み:</label>
                <div className='w-[280px]'>
                    <Select value={landmarkFilter} onValueChange={setLandmarkFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder='選択してください' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_LANDMARKS_SENTINEL}>すべての landmark</SelectItem>
                            {landmarks.map(lm => (
                                <SelectItem key={lm.id} value={String(lm.id)}>
                                    {lm.name}
                                    {' '}
                                    (#
                                    {lm.id}
                                    )
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {templates.length === 0 ?
                (
                    <div className='py-16 text-center text-gray-500'>
                        登録されているクーポンテンプレートがありません
                    </div>
                ) :
                (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-gray-200 text-left'>
                                    <th className='py-2 px-3'>ID</th>
                                    <th className='py-2 px-3'>landmark</th>
                                    <th className='py-2 px-3'>fortune</th>
                                    <th className='py-2 px-3'>名称</th>
                                    <th className='py-2 px-3'>有効期間</th>
                                    <th className='py-2 px-3 text-right'>発行</th>
                                    <th className='py-2 px-3 text-right'>消費</th>
                                    <th className='py-2 px-3 text-right'>残数</th>
                                    <th className='py-2 px-3 text-right'>在庫</th>
                                    <th className='py-2 px-3'>active</th>
                                    <th className='py-2 px-3'>レビュー後切替先</th>
                                    <th className='py-2 px-3 w-[220px]'>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templates.map(tpl => (
                                    <tr
                                        key={tpl.id}
                                        className={`border-b border-gray-100 hover:bg-gray-50 ${(tpl.available_count ?? 0) === 0 ? 'bg-red-50' : (tpl.low_stock_alert ? 'bg-amber-50' : '')}`}
                                    >
                                        <td className='py-2 px-3 text-gray-500'>{tpl.id}</td>
                                        <td className='py-2 px-3 text-xs text-gray-600'>
                                            {landmarkNameById[String(tpl.landmark_id)] ?? '-'}
                                            <span className='text-gray-400 ml-1'>
                                                (#
                                                {tpl.landmark_id}
                                                )
                                            </span>
                                        </td>
                                        <td className='py-2 px-3'>
                                            {formatFortune(tpl.fortune)}
                                        </td>
                                        <td className='py-2 px-3 font-medium'>
                                            <div className='flex flex-wrap items-center gap-1'>
                                                <span>{tpl.name}</span>
                                                {/* 2 段階 shake redesign (2026-05-16、 PR 6): 種別 / コード方式の badge */}
                                                {tpl.is_upgrade_only && (
                                                    <span className='px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-semibold'>
                                                        アップグレード
                                                    </span>
                                                )}
                                                {tpl.code_mode === 'unified' && (
                                                    <span className='px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold'>
                                                        統一
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className='py-2 px-3 text-xs text-gray-600'>
                                            {formatDate(tpl.valid_from)}
                                            {' '}
                                            ~
                                            {' '}
                                            {formatDate(tpl.valid_to)}
                                        </td>
                                        <td className='py-2 px-3 text-right'>
                                            {/* 2 段階 shake redesign (2026-05-16、 PR 6): unified は distribution_count を表示。
                                                個別 (= unique_per_distribution) は既存挙動 (total_issued) */}
                                            {tpl.code_mode === 'unified' ?
                                                (tpl.distribution_count ?? 0) :
                                                (tpl.total_issued ?? 0)}
                                        </td>
                                        <td className='py-2 px-3 text-right'>
                                            {/* unified は consumed という概念は無い (= distribution_count 増のみ)。
                                                統一の場合は '-' を表示。 */}
                                            {tpl.code_mode === 'unified' ?
                                                '-' :
                                                (tpl.total_consumed ?? 0)}
                                        </td>
                                        <td className='py-2 px-3 text-right text-emerald-700 font-medium'>
                                            {/* 残数 = 統一なら max - distributed、 個別なら total_issued - total_consumed */}
                                            {tpl.code_mode === 'unified' ?
                                                (tpl.max_distributions != null ?
                                                    Math.max(0, Number(tpl.max_distributions) - Number(tpl.distribution_count ?? 0)) :
                                                    '∞') :
                                                Math.max(0, (tpl.total_issued ?? 0) - (tpl.total_consumed ?? 0))}
                                        </td>
                                        <td className='py-2 px-3 text-right'>
                                            {(() => {
                                                // 2 段階 shake redesign (2026-05-16、 PR 6): 在庫表示を code_mode で分岐。
                                                //   - unified: 「N / 上限 M」 or 「N / 無制限」 + low_stock_alert 同様の閾値
                                                //   - unique: 既存 3 段階 (0=赤 / 1-19=黄 / 20+=緑、 PR 9 (Issue 3))
                                                if (tpl.code_mode === 'unified') {
                                                    const distributed = Number(tpl.distribution_count ?? 0);
                                                    const max = tpl.max_distributions != null ? Number(tpl.max_distributions) : null;
                                                    const remaining = max != null ? Math.max(0, max - distributed) : null;
                                                    if (max != null && remaining === 0) {
                                                        return (
                                                            <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs font-bold'>
                                                                配布完了
                                                            </span>
                                                        );
                                                    }
                                                    if (tpl.low_stock_alert) {
                                                        return (
                                                            <span className='inline-flex items-center gap-1 text-amber-700 font-medium text-xs'>
                                                                {`${distributed} / ${max}`}
                                                                <AlertTriangle className='w-3.5 h-3.5' aria-label='上限間近' />
                                                            </span>
                                                        );
                                                    }
                                                    return (
                                                        <span className='text-emerald-700 font-medium text-xs'>
                                                            {max != null ? `${distributed} / ${max}` : `${distributed} / 無制限`}
                                                        </span>
                                                    );
                                                }
                                                const n = tpl.available_count ?? 0;
                                                if (n === 0) {
                                                    return (
                                                        <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs font-bold'>
                                                            0 在庫切れ
                                                        </span>
                                                    );
                                                }
                                                if (tpl.low_stock_alert) {
                                                    return (
                                                        <span className='inline-flex items-center gap-1 text-amber-700 font-medium'>
                                                            {n}
                                                            <AlertTriangle className='w-3.5 h-3.5' aria-label='在庫不足' />
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <span className='text-emerald-700 font-medium'>{n}</span>
                                                );
                                            })()}
                                        </td>
                                        <td className='py-2 px-3'>{tpl.is_active ? '✓' : '×'}</td>
                                        <td className='py-2 px-3 text-xs text-gray-600'>
                                            {tpl.upgrade_target_template_id ?
                                                `#${tpl.upgrade_target_template_id}` :
                                                '-'}
                                        </td>
                                        <td className='py-2 px-3'>
                                            <div className='flex gap-2'>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleOpenUpload(tpl)}
                                                    title='コード入稿'
                                                >
                                                    <Upload className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleOpenList(tpl)}
                                                    title='コード一覧'
                                                >
                                                    <List className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleExport(tpl)}
                                                    title='CSV エクスポート (= POS マスタ用)'
                                                >
                                                    <Download className='w-4 h-4' />
                                                </Button>
                                                <Button variant='outline' size='sm' onClick={() => handleOpenEdit(tpl)}>
                                                    <Pencil className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleDelete(tpl)}
                                                    className='text-red-600 hover:text-red-800'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <CouponTemplateFormSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                template={editTarget}
                landmarks={landmarks}
                defaultLandmarkId={landmarkFilter === ALL_LANDMARKS_SENTINEL ? null : Number(landmarkFilter)}
                availableUpgradeTargets={templates}
                onSuccess={reload}
            />

            {/* Phase 2 PR 5 (2026-05-15): batch code 入稿 sheet */}
            <CouponBatchCodeUploadSheet
                open={uploadOpen}
                onOpenChange={setUploadOpen}
                template={uploadTarget}
                onSuccess={reload}
            />

            {/* Phase 2 PR 6 (2026-05-15): code 一覧 sheet */}
            <CouponBatchCodeListSheet
                open={listOpen}
                onOpenChange={setListOpen}
                template={listTarget}
                onChanged={reload}
            />
        </div>
    );
});
